/**
 * net/npc-geese.js — the four geese who are always at the carnival.
 *
 * A midway with exactly one goose in it reads as broken, and the public PeerJS
 * broker is not something to bet the first-run experience on. So the hub is
 * populated locally: four named geese waddle between stall fronts, the gate and
 * the prize desk, pause to watch, honk, and occasionally hop. They run online
 * and offline alike and they are pure visuals — no bodies, no colliders, no
 * ability to touch the player or a stall.
 *
 * ── Determinism ──
 * Every random draw happens once, at install, in a fixed loop order: names,
 * tints, cosmetics, then a full `NPC.planLength` waypoint plan per goose
 * (waypoint, pause length, lateral offset, honk flag, hop flag). Runtime just
 * walks that plan and draws nothing. Two loads with the same seed therefore
 * produce the same cast visiting the same places in the same order, even though
 * wall-clock timing — and so the exact positions at any instant — will differ
 * under SwiftShader. Tests assert the sequence, not the coordinates.
 *
 * @module net/npc-geese
 */

import * as THREE from 'three';
import { EVENTS, HUB, NPC, STALLS, stallPlayOrigin } from '../constants.js';
import { bus } from '../core/events.js';
import { mulberry32, options } from '../core/testmode.js';
import { GooseAvatar, GOOSE_TINTS, angleLerp } from './remote-goose.js';
import { pickNames } from './labels.js';

/**
 * The flock's own RNG stream, seeded from `options.seed` rather than drawn from
 * the shared `rng()`.
 *
 * Same generator, same seed, but a private stream — which matters because the
 * shared stream's position depends on how many numbers every other subsystem
 * happened to draw during boot first. Eight agents are adding modules to that
 * boot sequence in parallel; a cast of geese that changes because a stall agent
 * added a `rng()` call is not determinism worth having.
 *
 * Outside test mode the seed is mixed with the clock, so a real session gets a
 * fresh cast rather than the same four geese forever.
 */
function makeFlockRng() {
  const base = options.test ? options.seed : (options.seed ^ Date.now());
  return mulberry32((base ^ 0x9e3779b9) >>> 0);
}

/** Cosmetic ids NPCs draw from — greybox until the meta agent ships a catalog. */
const NPC_COSMETICS = Object.freeze({
  head: ['top-hat', 'party-cone', 'bowler', 'crown', null, null],
  body: ['scarf', 'bowtie', null, null],
  aura: ['sparkle', null, null, null],
});

/**
 * Every place an ambient goose might choose to stand.
 *
 * Stall waypoints sit `NPC.stallClearance` metres inboard of the play origin,
 * out in the walkway looking at the booth. A goose has no collider so it cannot
 * literally block an activation zone, but one standing on the exact spot you
 * walk to still *reads* as a blocker, and that is the thing worth avoiding.
 *
 * @returns {Array<{ id:string, x:number, z:number, facing:number }>}
 */
export function buildWaypoints() {
  const points = [];

  for (const spec of STALLS) {
    const origin = stallPlayOrigin(spec);
    const sign = spec.row === 'left' ? -1 : 1;
    points.push({
      id: `stall:${spec.id}`,
      x: sign * (HUB.playOriginX - NPC.stallClearance),
      z: origin.z,
      // Look at the booth: heading (sign, 0, 0) is yaw = sign · π/2.
      facing: sign * (Math.PI / 2),
    });
  }

  points.push({ id: 'gate', x: 0, z: HUB.spawn.z - 1.5, facing: Math.PI });
  points.push({
    id: 'prize-counter',
    x: HUB.prizeCounter.x,
    z: HUB.prizeCounter.z + 2.2,
    facing: Math.PI,
  });
  points.push({
    id: 'personal-space',
    x: HUB.personalSpacePortal.x,
    z: HUB.personalSpacePortal.z + 2.2,
    facing: Math.PI,
  });
  for (let i = 0; i < HUB.leaderboards.length; i++) {
    const b = HUB.leaderboards[i];
    const sign = b.x < 0 ? -1 : 1;
    points.push({
      id: `leaderboard-${i}`,
      x: b.x - sign * 1.6,
      z: b.z,
      facing: sign * (Math.PI / 2),
    });
  }

  return points;
}

/** One ambient goose: a visual body plus a pre-drawn plan and a tiny FSM. */
class NpcGoose {
  /**
   * @param {number} index
   * @param {{ name:string, tint:number, cosmetics:object,
   *           plan:Array<object>, waypoints:Array<object> }} spec
   */
  constructor(index, spec) {
    this.index = index;
    this.id = `npc-${index}`;
    this.waypoints = spec.waypoints;
    this.plan = spec.plan;
    this.planIndex = 0;
    /** Waypoint ids actually reached, in order — the determinism assertion. */
    this.visited = [];

    this.avatar = new GooseAvatar({
      name: spec.name,
      color: spec.tint,
      cosmetics: spec.cosmetics,
      tagColor: '#cfe8b0',
    });
    this.avatar.group.name = `npc-${index}-${spec.name}`;

    this.state = 'pausing';
    this.pauseLeft = 0;
    this._hopTime = -1;
    this._target = new THREE.Vector3();

    const first = this._step(0);
    this.avatar.position.set(first.x, 0, first.z);
    this.avatar.facing = first.facing;
    this.visited.push(first.id);

    // Commit to the next waypoint, then hold — the stagger has to be applied
    // after `_advance()`, which sets the state to walking.
    this._advance();
    this.state = 'pausing';
    this.pauseLeft = 1.5 + index * 0.9;
  }

  /** @returns {string} The waypoint id this goose is heading for. */
  get targetId() {
    return this._targetPoint ? this._targetPoint.id : null;
  }

  /**
   * Resolve plan entry `i` into a concrete waypoint with its lateral offset.
   * @param {number} i
   * @private
   */
  _step(i) {
    const entry = this.plan[i % this.plan.length];
    const wp = this.waypoints[entry.waypoint];
    return {
      id: wp.id,
      x: wp.x,
      // The offset runs along the midway so geese at the same stall stand in a
      // loose line rather than inside one another.
      z: wp.z + entry.offset,
      facing: wp.facing,
      pause: entry.pause,
      honk: entry.honk,
      hop: entry.hop,
    };
  }

  /** Commit to the next plan entry and start walking. @private */
  _advance() {
    this.planIndex++;
    this._targetPoint = this._step(this.planIndex);
    this._target.set(this._targetPoint.x, 0, this._targetPoint.z);
    this.state = 'walking';
  }

  /** @param {number} dt */
  update(dt) {
    if (this.state === 'walking') this._walk(dt);
    else this._pause(dt);

    if (this._hopTime >= 0) {
      this._hopTime += dt;
      const k = this._hopTime / NPC.hopTime;
      this.avatar.hopOffset = k >= 1 ? 0 : Math.sin(Math.PI * k) * NPC.hopHeight;
      if (k >= 1) this._hopTime = -1;
    }

    this.avatar.update(dt);
  }

  /** @private */
  _walk(dt) {
    const pos = this.avatar.position;
    const dx = this._target.x - pos.x;
    const dz = this._target.z - pos.z;
    const dist = Math.hypot(dx, dz);

    if (dist <= NPC.arriveRadius) {
      this._arrive();
      return;
    }

    const stepDistance = Math.min(NPC.speed * dt, dist);
    pos.x += (dx / dist) * stepDistance;
    pos.z += (dz / dist) * stepDistance;
    this.avatar.speed = NPC.speed;

    // Heading convention: yaw θ means moving along (sin θ, 0, cos θ).
    const desired = Math.atan2(dx, dz);
    this.avatar.facing = angleLerp(this.avatar.facing, desired, Math.min(1, dt * NPC.turnRate));
  }

  /** @private */
  _arrive() {
    const point = this._targetPoint;
    this.state = 'pausing';
    this.pauseLeft = point.pause;
    this.avatar.speed = 0;
    this.visited.push(point.id);
    if (this.visited.length > 64) this.visited.shift();

    if (point.honk) this.honk();
    if (point.hop) this._hopTime = 0;
  }

  /** @private */
  _pause(dt) {
    this.avatar.speed = 0;
    const facing = this._targetPoint ? this._targetPoint.facing : this.avatar.facing;
    this.avatar.facing = angleLerp(this.avatar.facing, facing, Math.min(1, dt * NPC.turnRate));
    this.pauseLeft -= dt;
    if (this.pauseLeft <= 0) this._advance();
  }

  /** Honk out loud, with a speech bubble to match. */
  honk() {
    this.avatar.say('HONK!', NPC.bubbleSeconds);
    bus.emit(EVENTS.AUDIO_PLAY, {
      name: 'honk',
      position: this.avatar.position.clone(),
      volume: 0.55,
    });
  }

  /** @returns {object} Test-facing snapshot. */
  snapshot() {
    return {
      id: this.id,
      name: this.avatar.name,
      position: this.avatar.position.toArray().map((n) => Math.round(n * 1000) / 1000),
      facing: Math.round(this.avatar.facing * 1000) / 1000,
      state: this.state,
      target: this.targetId,
      planIndex: this.planIndex,
      visited: this.visited.slice(),
      plan: this.plan.map((e) => this.waypoints[e.waypoint].id),
      cosmetics: Object.keys(this.avatar._cosmetics),
    };
  }

  dispose() {
    this.avatar.dispose();
  }
}

/** The ambient flock. One per page. */
class NpcFlock {
  constructor() {
    /** @type {NpcGoose[]} */ this.geese = [];
    /** @type {THREE.Group|null} */ this.root = null;
    this.waypoints = [];
    this.installed = false;
    /** @type {() => number} */ this.rand = makeFlockRng();
  }

  /** @param {number} a @param {number} b @returns {number} Integer in [a, b]. */
  _int(a, b) {
    return Math.floor(a + (b - a + 1) * this.rand());
  }

  /** @param {number} a @param {number} b @returns {number} Uniform in [a, b). */
  _range(a, b) {
    return a + (b - a) * this.rand();
  }

  /**
   * Build the flock and add it to the scene. Idempotent.
   * @param {THREE.Scene} scene
   * @returns {NpcFlock}
   */
  install(scene) {
    if (this.installed) return this;
    this.installed = true;

    this.root = new THREE.Group();
    this.root.name = 'npc-geese';
    scene.add(this.root);

    this.waypoints = buildWaypoints();
    // Geese start near the entrance so the first frame of the game has company
    // in it, rather than four dots 40 m down an empty midway.
    const nearIndices = this.waypoints
      .map((w, i) => ({ w, i }))
      .filter(({ w }) => w.z > -12)
      .map(({ i }) => i);

    const names = pickNames(NPC.count, this.rand);

    for (let i = 0; i < NPC.count; i++) {
      const plan = this._drawPlan(nearIndices);
      const goose = new NpcGoose(i, {
        name: names[i] || `Goose ${i + 1}`,
        tint: GOOSE_TINTS[i % GOOSE_TINTS.length],
        cosmetics: this._drawCosmetics(),
        plan,
        waypoints: this.waypoints,
      });
      this.geese.push(goose);
      this.root.add(goose.avatar.group);
    }

    return this;
  }

  /**
   * Draw one goose's whole itinerary up front. Everything random about an NPC
   * is decided here, which is what makes the runtime deterministic.
   * @param {number[]} nearIndices Waypoints acceptable as a starting point.
   * @private
   */
  _drawPlan(nearIndices) {
    const plan = [];
    for (let j = 0; j < NPC.planLength; j++) {
      let waypoint = j === 0 && nearIndices.length > 0
        ? nearIndices[this._int(0, nearIndices.length - 1)]
        : this._int(0, this.waypoints.length - 1);
      // Standing still for two consecutive entries looks like a stuck goose.
      if (j > 0 && waypoint === plan[j - 1].waypoint) {
        waypoint = (waypoint + 1) % this.waypoints.length;
      }
      plan.push({
        waypoint,
        pause: this._range(NPC.pauseMin, NPC.pauseMax),
        offset: this._range(-1.4, 1.4),
        honk: this.rand() < NPC.honkChance,
        hop: this.rand() < NPC.hopChance,
      });
    }
    return plan;
  }

  /** @private */
  _drawCosmetics() {
    const out = {};
    for (const [slot, ids] of Object.entries(NPC_COSMETICS)) {
      const id = ids[this._int(0, ids.length - 1)];
      if (id) out[slot] = id;
    }
    return out;
  }

  /** @param {number} dt */
  update(dt) {
    for (const g of this.geese) g.update(dt);
  }

  /** @returns {Array<object>} One entry per goose — the `__cc.npc.list()` shape. */
  list() {
    return this.geese.map((g) => ({
      id: g.id,
      name: g.avatar.name,
      position: g.avatar.position.toArray(),
      facing: g.avatar.facing,
      state: g.state,
      target: g.targetId,
    }));
  }

  /** @returns {object} Full deterministic snapshot for tests. */
  state() {
    return {
      installed: this.installed,
      count: this.geese.length,
      // Coordinates included so the stall-clearance rule is assertable without
      // a test needing to import the hub layout.
      waypoints: this.waypoints.map((w) => ({ id: w.id, x: w.x, z: w.z })),
      geese: this.geese.map((g) => g.snapshot()),
    };
  }

  /** Make a named goose honk on demand — used by tests and the visit flow. */
  honk(index = 0) {
    const g = this.geese[index];
    if (!g) return false;
    g.honk();
    return true;
  }

  dispose() {
    for (const g of this.geese) g.dispose();
    this.geese = [];
    this.root?.removeFromParent();
    this.root = null;
    this.installed = false;
  }
}

/** The one ambient flock. */
export const npcGeese = new NpcFlock();
